import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiService } from 'src/app/shared/services/api.service';
import { CadDrawing, CadDrawingContextType, CadViewerService } from 'src/app/shared/services/cad-viewer.service';
import { CadViewerDialogService } from 'src/app/shared/services/cad-viewer-dialog.service';

export const CAD_FILE_ACCEPT = '.dwg,.dxf';

/**
 * Upload / list / view / delete CAD drawings for one context
 * (standalone page, project, LRS parcel, GIS).
 */
@Component({
  selector: 'app-cad-drawing-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './cad-drawing-list.component.html',
  styleUrls: ['./cad-drawing-list.component.css'],
})
export class CadDrawingListComponent implements OnChanges {
  @Input({ required: true }) contextType!: CadDrawingContextType;
  @Input() contextId: string | number | null = null;
  @Input() projectId: number | null = null;
  @Input() title = 'CAD drawings';
  @Input() canEdit = true;
  @Input() compact = false;
  @Input() limit = 50;
  /** Emit `select` instead of opening the viewer dialog (host renders its own canvas). */
  @Input() selectMode = false;
  @Input() selectedId: number | null = null;

  @Output() select = new EventEmitter<CadDrawing>();
  @Output() drawingsChange = new EventEmitter<CadDrawing[]>();

  readonly accept = CAD_FILE_ACCEPT;
  drawings: CadDrawing[] = [];
  loading = false;
  uploading = false;
  error = '';
  dwgSupported = true;

  constructor(
    private api: ApiService,
    private viewerDialog: CadViewerDialogService,
    cadViewer: CadViewerService,
    private cdr: ChangeDetectorRef
  ) {
    cadViewer.health().subscribe((h) => {
      this.dwgSupported = !h.reachable || h.dwgSupported;
      this.cdr.markForCheck();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['contextType'] || changes['contextId'] || changes['projectId']) {
      this.reload();
    }
  }

  reload(): void {
    if (!this.contextType) return;
    this.loading = true;
    this.error = '';
    this.api
      .listCadDrawings({
        contextType: this.contextType,
        contextId: this.contextId,
        projectId: this.projectId,
        limit: this.limit,
      })
      .subscribe({
        next: (rows) => {
          this.drawings = rows;
          this.loading = false;
          this.drawingsChange.emit(rows);
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.error = 'Could not load drawings.';
          this.cdr.markForCheck();
        },
      });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    this.uploading = true;
    this.error = '';
    this.api
      .uploadCadDrawing(file, {
        contextType: this.contextType,
        contextId: this.contextId,
        projectId: this.projectId,
      })
      .subscribe({
        next: (drawing) => {
          this.uploading = false;
          this.drawings = [drawing, ...this.drawings];
          this.drawingsChange.emit(this.drawings);
          this.cdr.markForCheck();
          this.open(drawing);
        },
        error: (err) => {
          this.uploading = false;
          this.error = err?.error?.error || 'Upload failed.';
          this.cdr.markForCheck();
        },
      });
  }

  open(drawing: CadDrawing): void {
    if (this.selectMode) {
      this.select.emit(drawing);
      return;
    }
    this.viewerDialog.open({ drawing });
  }

  remove(drawing: CadDrawing, event: Event): void {
    event.stopPropagation();
    if (!confirm(`Delete drawing "${drawing.original_name}"?`)) return;
    this.api.deleteCadDrawing(drawing.id).subscribe({
      next: () => {
        this.drawings = this.drawings.filter((d) => d.id !== drawing.id);
        this.drawingsChange.emit(this.drawings);
        this.cdr.markForCheck();
      },
      error: () => {
        this.error = 'Delete failed.';
        this.cdr.markForCheck();
      },
    });
  }

  formatSize(bytes: number | null): string {
    if (bytes == null) return '';
    if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
